import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';

const FLOOR_OPTIONS = ['Basement', 'Lower Ground', 'Ground Floor', 'First Floor', 'Second Floor', 'Third Floor', 'Fourth Floor', 'Attic'];
const USE_OPTIONS = ['Bedroom', 'Reception', 'Kitchen', 'Bathroom', 'WC', 'En-suite', 'Utility', 'Hallway', 'Landing', 'Study', 'Dining', 'Storage', 'Other'];
const CONDITION_OPTIONS = ['Good', 'Fair', 'Poor', 'Requires Modernisation'];
const LOCATION_OPTIONS = ['internal', 'balcony', 'terrace', 'yard', 'garage', 'store'];

const emptyRow = {
  room_name: '', floor_level: 'Ground Floor', length: '', width: '', area: '',
  use: 'Bedroom', notes: '', condition_note: '',
  location_type: 'internal', included_in_valuation: true,
};

export default function RoomScheduleTable({ rows, onAdd, onUpdate, onDelete }) {
  const [newRow, setNewRow] = useState({ ...emptyRow });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const handleAdd = () => {
    if (!newRow.room_name) return;
    const payload = {
      ...newRow,
      length: parseFloat(newRow.length) || 0,
      width: parseFloat(newRow.width) || 0,
      area: parseFloat(newRow.area) || 0,
    };
    onAdd(payload);
    setNewRow({ ...emptyRow });
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditData({ ...row });
  };

  const saveEdit = () => {
    onUpdate(editId, {
      ...editData,
      length: parseFloat(editData.length) || 0,
      width: parseFloat(editData.width) || 0,
      area: parseFloat(editData.area) || 0,
    });
    setEditId(null);
  };

  return (
    <div className="space-y-3" data-testid="room-schedule-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-2 font-medium text-gray-600">Room</th>
              <th className="text-left p-2 font-medium text-gray-600">Floor</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">L (m)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">W (m)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-24">Area (m²)</th>
              <th className="text-left p-2 font-medium text-gray-600">Use</th>
              <th className="text-left p-2 font-medium text-gray-600">Location</th>
              <th className="text-center p-2 font-medium text-gray-600 w-16">Valued</th>
              <th className="text-left p-2 font-medium text-gray-600">Notes</th>
              <th className="p-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b hover:bg-gray-50/50" data-testid={`room-row-${row.id}`}>
                {editId === row.id ? (
                  <>
                    <td className="p-1"><Input className="h-7 text-xs" value={editData.room_name || ''} onChange={e => setEditData({...editData, room_name: e.target.value})} /></td>
                    <td className="p-1">
                      <Select value={editData.floor_level || ''} onValueChange={v => setEditData({...editData, floor_level: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{FLOOR_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.length || ''} onChange={e => setEditData({...editData, length: e.target.value})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.width || ''} onChange={e => setEditData({...editData, width: e.target.value})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.area || ''} onChange={e => setEditData({...editData, area: e.target.value})} /></td>
                    <td className="p-1">
                      <Select value={editData.use || ''} onValueChange={v => setEditData({...editData, use: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{USE_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1">
                      <Select value={editData.location_type || 'internal'} onValueChange={v => setEditData({...editData, location_type: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 text-center">
                      <input type="checkbox" checked={editData.included_in_valuation !== false} onChange={e => setEditData({...editData, included_in_valuation: e.target.checked})} />
                    </td>
                    <td className="p-1"><Input className="h-7 text-xs" value={editData.notes || ''} onChange={e => setEditData({...editData, notes: e.target.value})} /></td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-2 font-medium">{row.room_name}</td>
                    <td className="p-2 text-gray-500">{row.floor_level}</td>
                    <td className="p-2 text-right font-mono">{row.length || '-'}</td>
                    <td className="p-2 text-right font-mono">{row.width || '-'}</td>
                    <td className="p-2 text-right font-mono font-medium">{row.area ? row.area.toFixed(2) : '-'}</td>
                    <td className="p-2 text-gray-500">{row.use}</td>
                    <td className="p-2 text-gray-500 capitalize">{row.location_type || 'internal'}</td>
                    <td className="p-2 text-center">{row.included_in_valuation !== false ? '✓' : '—'}</td>
                    <td className="p-2 text-gray-400 text-xs truncate max-w-[120px]">{row.notes || '-'}</td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEdit(row)} data-testid={`edit-room-${row.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onDelete(row.id)} data-testid={`delete-room-${row.id}`}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Add new row */}
            <tr className="border-b bg-blue-50/30">
              <td className="p-1"><Input className="h-7 text-xs" placeholder="Room name" value={newRow.room_name} onChange={e => setNewRow({...newRow, room_name: e.target.value})} data-testid="new-room-name" /></td>
              <td className="p-1">
                <Select value={newRow.floor_level} onValueChange={v => setNewRow({...newRow, floor_level: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FLOOR_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="L" value={newRow.length} onChange={e => setNewRow({...newRow, length: e.target.value})} data-testid="new-room-length" /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="W" value={newRow.width} onChange={e => setNewRow({...newRow, width: e.target.value})} data-testid="new-room-width" /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="Area" value={newRow.area} onChange={e => setNewRow({...newRow, area: e.target.value})} data-testid="new-room-area" /></td>
              <td className="p-1">
                <Select value={newRow.use} onValueChange={v => setNewRow({...newRow, use: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{USE_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1">
                <Select value={newRow.location_type} onValueChange={v => setNewRow({...newRow, location_type: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1 text-center">
                <input type="checkbox" checked={newRow.included_in_valuation !== false} onChange={e => setNewRow({...newRow, included_in_valuation: e.target.checked})} />
              </td>
              <td className="p-1"><Input className="h-7 text-xs" placeholder="Notes" value={newRow.notes} onChange={e => setNewRow({...newRow, notes: e.target.value})} /></td>
              <td className="p-1">
                <Button size="sm" variant="default" className="h-7 w-7 p-0" onClick={handleAdd} data-testid="add-room-btn"><Plus className="h-3.5 w-3.5" /></Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
